import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** Prevent duplicate bindings */
  #termsInitialized = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    // Initialize T&C logic when drawer opens
    this.#initTermsLogic();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * TERMS & CONDITIONS LOGIC
   */
  #initTermsLogic() {
    if (this.#termsInitialized) return;

    const checkbox = this.querySelector('#CartTermsCheckbox');
    const checkoutBtn = this.querySelector('.custom-checkout-btn');
    const attrInput = this.querySelector('#termsDrawerAttr');

    if (!checkbox || !checkoutBtn) return;

    // Set initial button state
    checkoutBtn.disabled = !checkbox.checked;

    checkbox.addEventListener('change', () => {
      checkoutBtn.disabled = !checkbox.checked;

      // Save cart attribute if field exists
      if (attrInput) {
        attrInput.value = checkbox.checked ? 'Yes' : 'No';
      }
    });

    checkoutBtn.addEventListener('click', (e) => {
      if (!checkbox.checked) {
        e.preventDefault();
        return;
      }
      window.location.href = '/checkout';
    });

    this.#termsInitialized = true;
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
